
import React, { Fragment, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { Col, Row, Tab } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";

import History from "@/Helpers/History";
import { getCnCWeekNavigator } from "@/Helpers/CncHelper";

import { usePlan } from "@/Hooks/UsePlan";
import useTranslation from "@/Hooks/UseTranslation";
import { useProject } from "@/Hooks/UseProject";

import InterfaceActions from "@/Redux/Actions/InterfaceActions";
import ProjectCNCActions from "@/Redux/Actions/ProjectCNCActions";
import { loadIndicatorsPPCTasksAction } from "@/Redux/Actions/IndicatorsPPCActions";

import ProjectLayout from "@/Layouts/MainLayout/ProjectLayout";
import ContentBody from "@/Layouts/MainLayout/components/ContentBody";

import CncEditor from "@/Components/CncEditor/CncEditor";
import ContentBodyHeader from "@/Components/ResourcesCncPageComponents/ContentBodyHeader/ContentBodyHeader";
import CncTypesList from "@/Components/ResourcesCncPageComponents/CncTypesList/CncTypesList";

import AddICon from '@/Components/SvgImages/Add'
import CncList from "@/Components/ResourcesCncPageComponents/CncList/CncList";

import './ProjectResourcesCNCPage.scss'

const ProjectResourcesCNCPage = (props: any) => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const [cncEditor, setCncEditor] = useState({
    show: false,
    row: {},
    saving: false,
  })

  const [showingFilters, setShowingFilters] = useState(false)
  const [tab, setTab] = useState('list')

  const week = props.match.params.week

  const { plan, showCantCreateCustomCNCsModal } = usePlan()
  const { project_id, project } = useProject()

  const projectLoadStatus = useSelector((state: any) => state.ProjectsReducer.loadRowStatus)

  const resourcesCNC = useSelector((state: any) => state.ResourcesCNCReducer)
  // TODO: eval passing this into a new resourcesCNCReducer file
  const projectCNC = useSelector((state: any) => state.ProjectCNCReducer)

  const navigator = useMemo(() => {

    if (!project._id) return {
      prev: '',
      next: '',
      week: 0,
    }

    return getCnCWeekNavigator(project.startsAt, project.realEnd, week)
  }, [project, week])

  const navigateToWeek = (week?: any) => {

    if (week) {

      History.push(`/project/${project_id}/resources/cnc/${week}`)
    } else {

      History.push(`/project/${project_id}/resources/cnc`)
    }
  }

  const loadProjectCNC = async () => {

    await dispatch(ProjectCNCActions.loadResult(project._id))
  }

  const loadNotCompleted = async () => {

    await dispatch(loadIndicatorsPPCTasksAction(project._id, navigator.week.toString()))
  }

  const getWeekNumber = () => {

    const weekStart = moment(project.startsAt)

    const lastWeek = moment(navigator.week, 'YYYYWW')

    return lastWeek.diff(weekStart, 'weeks') + 1
  }

  const getWeekRange = () => {

    const weekMoment = moment(navigator.week, 'YYYYWW')

    return weekMoment.startOf('week').format('DD/MM/YYYY') + ' - ' + weekMoment.endOf('week').format('DD/MM/YYYY')
  }

  const saveCnc = async (cnc: any) => {

    await dispatch(InterfaceActions.showLoader())

    cnc.project = project._id

    setCncEditor({
      ...cncEditor,
      saving: true,
    })

    if (!cnc._id) {

      await dispatch(ProjectCNCActions.createRow(cnc))
    }

    setCncEditor({
      ...cncEditor,
      show: false,
      row: {},
      saving: false,
    })

    await loadProjectCNC()

    dispatch(InterfaceActions.hideLoader())
  }

  const handleCreateCustomCNC = () => {

    if (plan.cannot('create', 'CustomCNC')) {

      showCantCreateCustomCNCsModal(t('RESOURCES_CNC:CantCreateCustomCNCs'))
      return
    }

    setCncEditor({ ...cncEditor, show: true })
  }

  useEffect(() => {

    if (projectLoadStatus === 'loaded' && projectCNC.loadResultStatus === 'none') {

      loadProjectCNC()
    }
  }, [projectLoadStatus, projectCNC.loadResultStatus])

  useEffect(() => {

    if (projectLoadStatus !== 'loaded') return

    const {
      loadStatus,
      week,
    } = resourcesCNC.notCompleted

    if (loadStatus === 'none' && loadStatus !== 'loading' || week !== navigator.week.toString()) {

      loadNotCompleted()
    }
  }, [projectLoadStatus, resourcesCNC.notCompleted.loadStatus, navigator.week])

  useEffect(() => {

    const projectLoaded = projectLoadStatus == 'loaded'
    const projectCNCsLoaded = projectCNC.loadResultStatus === 'loaded'
    const notCompletedLoaded = resourcesCNC.notCompleted.loadStatus === 'loaded'

    if (!projectLoaded || !projectCNCsLoaded || !notCompletedLoaded) {

      dispatch(InterfaceActions.showLoader())
    } else {

      dispatch(InterfaceActions.hideLoader())
    }
  }, [projectLoadStatus, projectCNC.loadResultStatus, resourcesCNC.notCompleted.loadStatus])

  return (
    <ProjectLayout menuName="resources" subMenuName="cnc">
      <ContentBody noScroll>
        <ContentBodyHeader
          className="pl4-resources-cnc-header"
          title={(
            <Fragment>
              <h3 className="h3-title">
                <span className="main">
                  {t('RESOURCES_CNC:Title')}
                </span>
                {!!navigator.week && (
                  <>
                    <span className="separator">|</span>
                    <span className="sub">
                      {t('RESOURCES_CNC:TitleWeek')} {getWeekNumber()} - {getWeekRange()}
                    </span>
                  </>
                )}
              </h3>
            </Fragment>
          )}
          weekNavigator={tab === 'list' ? navigator : undefined}
          onChangeWeek={navigateToWeek}
          buttons={[
            {
              icon: <AddICon fill="#fff" height={24} width={24} />,
              label: t('RESOURCES_CNC:ToolbarAddCNCButton'),
              onClick: () => handleCreateCustomCNC(),
              visible: tab === 'config',
              active: showingFilters
            },
          ]}
          tabs={[
            {
              icon: <span className="icon-tablero"></span>,
              key: 'list',
              label: t('RESOURCES_CNC:ToolbarNotCompletedTab')
            },
            {
              icon: <span className="icon-configuracion"></span>,
              key: 'config',
              label: t('RESOURCES_CNC:ToolbarSettingsTab')
            }
          ]}
          activeTabKey={tab}
          onChangeTab={setTab}
        />
        <Row>
          <Col>
            <Tab.Content className="no-padding mb-2">
              <Tab.Pane active={tab === 'list'}>
                <CncList
                  list={resourcesCNC.notCompleted.result}
                  assigningList={resourcesCNC.assigningList}
                  loadStatus={resourcesCNC.notCompleted.loadStatus}
                  onUpdateList={loadNotCompleted}
                />
              </Tab.Pane>
              <Tab.Pane active={tab === 'config'}>
                <CncTypesList
                  list={projectCNC.result}
                  onUpdateList={loadProjectCNC}
                />
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </ContentBody>

      {cncEditor.show && (
        <CncEditor
          show={cncEditor.show}
          row={cncEditor.row}
          result={projectCNC.result}
          saving={cncEditor.saving}
          onHide={() => setCncEditor({ ...cncEditor, show: false })}
          onSave={(cnc: any) => saveCnc(cnc)}
        />
      )}
    </ProjectLayout>
  )
}

export default ProjectResourcesCNCPage
